import { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import DocBrandBand, { ScopeSections } from '../../components/DocBrandBand';
import SignaturePad from '../../components/SignaturePad';
import { Card, SectionTitle, Field, Spinner, ErrorNote } from '../../components/ui';
import { money, fmtDateTime, normSection, proposalTotals, legacyTotal } from '../../lib/format';

export default function PublicAgreement() {
  const { token } = useParams();
  const { data, error, loading, reload } = useAsync(() => publicApi.get('/a/' + token), [token]);
  const padRef = useRef();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [padEmpty, setPadEmpty] = useState(true);

  if (loading) return <Spinner label="Loading your agreement" />;
  if (error) {
    return (
      <div className="view">
        <ErrorNote onRetry={reload}>
          We couldn’t open this agreement. The link may have expired — please contact us for a fresh one.
        </ErrorNote>
      </div>
    );
  }

  const { agreement: a, company: c, customer: cu, proposal: p } = data;
  const signed = a.status === 'signed';

  const sections = p ? (p.sections || []).map(normSection) : [];
  const t = p ? proposalTotals(sections, p.taxPct, legacyTotal(p)) : null;

  async function sign() {
    if (!name.trim()) return setFormError('Please type your full name.');
    if (padRef.current?.isEmpty()) return setFormError('Please draw your signature.');
    setBusy(true); setFormError('');
    try {
      await publicApi.post(`/a/${token}/sign`, {
        signerName: name.trim(),
        signatureDataUrl: padRef.current.toDataURL(),
      });
      reload();
    } catch (e) {
      setFormError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="doc">
      <DocBrandBand label="Agreement" company={c} meta={[
        { label: 'Agreement No.', value: a.number || '—' },
        { label: 'For', value: cu?.name || '' },
      ]} />

      <div className="view">
        {p ? (
          <Card tight>
            <SectionTitle>Scope (from proposal #{p.number})</SectionTitle>
            <ScopeSections sections={sections} />
            {t.hasBreakdown ? (
              <>
                <div className="doc-line" style={{ marginTop: 'var(--s-3)' }}>
                  <span className="d sb">Subtotal</span><span className="a">{money(t.subtotal)}</span>
                </div>
                <div className="doc-line"><span className="d sb">Tax ({t.taxPct}%)</span><span className="a">{money(t.tax)}</span></div>
              </>
            ) : null}
            <div className="doc-total"><span className="nm">Total</span><span className="v">{money(t.total)}</span></div>
          </Card>
        ) : null}

        <Card tight>
          <SectionTitle>Terms &amp; conditions</SectionTitle>
          <p className="doc-prose">{a.termsText}</p>
        </Card>

        <Card tight>
          <SectionTitle>Payment schedule</SectionTitle>
          <p className="doc-prose">{a.paymentScheduleText}</p>
        </Card>

        {a.exclusions ? (
          <Card tight><SectionTitle>Exclusions</SectionTitle><p className="doc-prose">{a.exclusions}</p></Card>
        ) : null}

        {signed ? (
          <>
            <div className="note ok">
              <b>✓ Signed</b> by {a.signature?.name} on {fmtDateTime(a.signedAt)}.
            </div>
            {a.signature?.dataUrl ? (
              <Card tight>
                <div className="sb">Signature</div>
                <img src={a.signature.dataUrl} alt={`Signature of ${a.signature.name}`} style={{ maxWidth: '100%' }} />
              </Card>
            ) : null}
          </>
        ) : (
          <Card>
            <SectionTitle sub="Draw your signature and type your name to sign.">Sign agreement</SectionTitle>
            <div style={{ marginTop: 'var(--s-3)' }}>
              <SignaturePad ref={padRef} onChange={setPadEmpty} />
            </div>
            <div style={{ marginTop: 'var(--s-3)' }}>
              <Field label="Your full name" id="signer" error={formError}>
                <input id="signer" className="fi" value={name} onChange={e => setName(e.target.value)}
                  autoComplete="name" placeholder="Type your full name" />
              </Field>
            </div>
            <p className="sb" style={{ marginTop: 'var(--s-2)' }}>
              By signing you agree to the terms above. Your signature, name, time and IP address are recorded.
            </p>
            <button className="btn pri block" style={{ marginTop: 'var(--s-3)' }}
              onClick={sign} disabled={busy || padEmpty || !name.trim()}>
              {busy ? 'Signing…' : '✓ Sign & agree'}
            </button>
          </Card>
        )}
      </div>

      <div className="actions">
        {/* Offered after signing too — the PDF then carries the signature
            block, which is the copy worth keeping. */}
        <a className="btn no-print" href={`/api/a/${token}/pdf?download=1`}>📄 Download PDF</a>
        <button className="btn no-print" onClick={() => window.print()}>🖨 Print</button>
      </div>
    </article>
  );
}
